import React, { useEffect, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';

import { Tooltip } from 'uiToolkitV1/Tooltip';

import { validateAndUpdateAccessControl } from '../../../helpers/validateAccessControl';
import {
  updateAccessControl,
  useAccessControlSelector,
  useTemplateMetaDataSelector,
} from '../../../slice/accessControlSlice';
import { AccessControlTableType } from '../../../types';
import MultiSelect from '../MultiSelect';

interface Props {
  colDef: Record<string, any>;
  data: Record<string, any>;
  tableType: AccessControlTableType;
}

const MultiValueSelect = ({ colDef, data, tableType }: Props) => {
  const { field } = colDef ?? {};
  const { dropdownValues = [], permittedValues, values = [] } = data[field] ?? {};

  const { isEditMode } = useTemplateMetaDataSelector();

  const { value: role } = data ?? {};

  const selectedValues = dropdownValues?.filter((item) => values.includes(item.value));

  const dispatch = useDispatch();

  const permittedDropdownValues = dropdownValues?.map((item) => ({
    ...item,
    isDisabled: !permittedValues?.includes(item.value),
    isHidden: item?.value === 'NONE',
  }));

  const { accessControl } = useAccessControlSelector();

  // Create a useRef to track if the effect has already run
  // this makes sure that the effect only runs once after onChange
  // as we can't get the latest value after dispatch in onChange
  // and we need the latest value to validate the access control
  const effectRun = useRef(true);

  const onChange = (selectedValue = []) => {
    if (tableType === 'rolesToCreateConfig') {
      // selecting the selectedValue with highest length of valuesToSelect
      const highestValuesToSelect = selectedValue.reduce((acc, curr) => {
        if (curr.valuesToSelect.length > acc.valuesToSelect.length) {
          return curr;
        }
        return acc;
      }, selectedValue[0]);

      const { valuesToSelect } = highestValuesToSelect ?? {};
      const updatedValues = valuesToSelect;
      dispatch(
        updateAccessControl({
          privilegeKey: field,
          tableType,
          role,
          isMultiSelect: true,
          value: updatedValues,
        })
      );
      effectRun.current = false;
    } else if (tableType === 'rolesToUploadInsightsCustomTagConfig') {
      const updatedValues = selectedValue.map((item) => item.value);
      dispatch(
        updateAccessControl({
          privilegeKey: field,
          tableType,
          role,
          isMultiSelect: true,
          value: updatedValues,
        })
      );
      effectRun.current = false;
    }
  };

  useEffect(() => {
    if (isEditMode && !effectRun.current) {
      validateAndUpdateAccessControl(accessControl, dispatch);
      effectRun.current = true;
    }
  }, [isEditMode, accessControl, dispatch, effectRun]);

  const isGoalAdminRow = role === 'GOAL_ADMIN';

  const isDisabled = !isEditMode || isGoalAdminRow;

  const showTooltip = isGoalAdminRow && isEditMode;

  return (
    <Tooltip label={showTooltip ? 'Admin access cannot be changed' : ''}>
      <Box
        width="100%"
        sx={{
          '.select': {
            width: '100%',
          },
        }}
      >
        <MultiSelect
          onSelect={(newSelectedItems) => {
            onChange(newSelectedItems);
          }}
          menuItems={permittedDropdownValues}
          selectedItems={selectedValues}
          // permittedValues={permittedDropdownValues}
          isReadOnly={isDisabled}
        />
      </Box>
    </Tooltip>
  );
};

export { MultiValueSelect };
